import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';


export default function SuperAdminPanel() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [form, setForm] = useState({
    instituteName: '',
    adminEmail: '',
    adminPassword: '',
    logoUrl: '',
    themeColor: '#3b82f6',
    role: 'institute',
    parentTenantEmail: '',
  });

  const [editingTenant, setEditingTenant] = useState(null);
  const [editForm, setEditForm] = useState({
    schoolName: '',
    logoUrl: '',
    themeColor: '#3b82f6',
    role: 'institute',
    parentTenantEmail: '',
  });

  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [aiProvider, setAiProvider] = useState('gemini');

  // Global WhatsApp Config State
  const [globalWaConfig, setGlobalWaConfig] = useState({
    whatsappApiKey: '',
    whatsappPhoneNumberId: '',
    whatsappBusinessAccountId: '',
    whatsappAutoNotifyEnabled: false,
    whatsappStudyTimeTemplate: '',
    metaWebhookVerifyToken: ''
  });
  const [savingWaConfig, setSavingWaConfig] = useState(false);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);

  const fetchGlobalWaConfig = async () => {
    try {
      const res = await axios.get(API_BASE_URL + '/api/superadmin/whatsapp-config');
      if (res.data) {
        setGlobalWaConfig({
          whatsappApiKey: res.data.whatsappApiKey || '',
          whatsappPhoneNumberId: res.data.whatsappPhoneNumberId || '',
          whatsappBusinessAccountId: res.data.whatsappBusinessAccountId || '',
          whatsappAutoNotifyEnabled: res.data.whatsappAutoNotifyEnabled || false,
          whatsappStudyTimeTemplate: res.data.whatsappStudyTimeTemplate || '',
          metaWebhookVerifyToken: res.data.metaWebhookVerifyToken || ''
        });
      }
    } catch (err) {
      console.error("fetchGlobalWaConfig Error:", err);
    }
  };

  const fetchWhatsappTemplates = async () => {
    try {
      const res = await axios.get(API_BASE_URL + '/api/superadmin/whatsapp-templates');
      setWhatsappTemplates(res.data || []);
    } catch (err) {
      console.error("fetchWhatsappTemplates Error:", err);
    }
  };

  const saveGlobalWaConfig = async () => {
    setSavingWaConfig(true);
    try {
      await axios.post(API_BASE_URL + '/api/superadmin/whatsapp-config', globalWaConfig);
      toast.success('Global WhatsApp Config saved successfully');
    } catch (err) {
      toast.error('Failed to save Global WhatsApp Config');
      console.error(err);
    } finally {
      setSavingWaConfig(false);
    }
  };

  const [sendingStudyTime, setSendingStudyTime] = useState(false);

  const handleSendStudyTime = async () => {
    if (!globalWaConfig.whatsappStudyTimeTemplate) {
      toast.error('Please select a study time template first');
      return;
    }
    if (!window.confirm('This will broadcast the study time template to ALL active students across all tenants. Are you sure you want to proceed?')) {
      return;
    }

    try {
      setSendingStudyTime(true);
      
      // Auto-save the current configuration before broadcasting to ensure the backend has the selected template
      await axios.post(API_BASE_URL + '/api/superadmin/whatsapp-config', globalWaConfig);

      const res = await axios.post(`${API_BASE_URL}/api/superadmin/broadcast-study-time`);
      toast.success(res.data.message || 'Broadcast completed successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data || 'Failed to send broadcast');
    } finally {
      setSendingStudyTime(false);
    }
  };


  // Assignment State
  const [selectedTenantForAssignment, setSelectedTenantForAssignment] = useState('');
  const [tenantStudents, setTenantStudents] = useState([]);
  const [tenantTeachers, setTenantTeachers] = useState([]);
  const [tenantAssignments, setTenantAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  const fetchTenantAssignmentData = async (tenantEmail) => {
    if (!tenantEmail) {
      setSelectedTenantForAssignment('');
      return;
    }
    setLoadingAssignments(true);
    try {
      const [studentsRes, teachersRes, assignmentsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/superadmin/tenant/${tenantEmail}/students`),
        axios.get(`${API_BASE_URL}/api/superadmin/tenant/${tenantEmail}/teachers`),
        axios.get(`${API_BASE_URL}/api/superadmin/tenant/${tenantEmail}/assignments`)
      ]);
      setTenantStudents(studentsRes.data);
      setTenantTeachers(teachersRes.data);
      setTenantAssignments(assignmentsRes.data);
      setSelectedTenantForAssignment(tenantEmail);
    } catch (err) {
      console.error('Fetch assignment data error:', err.response?.data || err);
      toast.error(`Failed to fetch assignment data: ${err.response?.data?.message || err.message}`);
      setTenantStudents([]);
      setTenantTeachers([]);
      setTenantAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const toggleStudentWhatsapp = async (studentId) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/api/superadmin/tenant/${selectedTenantForAssignment}/student/${studentId}/toggle-whatsapp`);
      if (res.data.status === 'success') {
        toast.success(res.data.message);
        setTenantStudents(prev => prev.map(s => 
          s.id === studentId ? { ...s, whatsappEnabled: !s.whatsappEnabled } : s
        ));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to toggle WhatsApp notification');
    }
  };

  const toggleTeacherAssignment = async (studentId, studentEmail, teacherEmail) => {
    const currentAssigned = tenantAssignments
      .filter(a => a.student_email === studentEmail)
      .map(a => a.teacher_email);
    
    let newAssigned;
    if (currentAssigned.includes(teacherEmail)) {
      newAssigned = currentAssigned.filter(e => e !== teacherEmail);
    } else {
      newAssigned = [...currentAssigned, teacherEmail];
    }

    try {
      const res = await axios.post(`${API_BASE_URL}/api/superadmin/assign-teachers`, {
        tenantEmail: selectedTenantForAssignment,
        studentId: studentId,
        studentEmail: studentEmail,
        teacherEmails: newAssigned
      });
      if (res.data.status === 'success') {
        toast.success(res.data.message);
        const assignmentsRes = await axios.get(`${API_BASE_URL}/api/superadmin/tenant/${selectedTenantForAssignment}/assignments`);
        setTenantAssignments(assignmentsRes.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign teachers');
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await axios.get(API_BASE_URL + '/api/superadmin/config');
      const aiConfig = res.data.find(c => c.configKey === 'ai_provider');
      if (aiConfig) setAiProvider(aiConfig.configValue);
    } catch (err) {
      console.error("fetchConfig Error:", err);
    }
  };

  const updateConfig = async (val) => {
    try {
      setAiProvider(val);
      await axios.post(API_BASE_URL + '/api/superadmin/config', {
        configKey: 'ai_provider',
        configValue: val
      });
      toast.success(`AI Provider switched to ${val.toUpperCase()}`);
    } catch (err) {
      toast.error('Failed to update AI configuration');
    }
  };

  const fetchTenants = async () => {
    try {
      setLoadingTenants(true);
      const res = await axios.get(API_BASE_URL + '/api/superadmin/tenants');
      setTenants(res.data);
    } catch (err) {
      console.error("fetchTenants Error:", err);
      toast.error('Failed to load tenants list: ' + err.message);
    } finally {
      setLoadingTenants(false);
    }
  };

  useEffect(() => {
    fetchTenants();
    fetchConfig();
    fetchGlobalWaConfig();
    fetchWhatsappTemplates();
  }, []);

  const handleDeleteTenant = async (tenantEmail) => {
    if (!window.confirm(`Are you sure you want to completely delete the tenant '${tenantEmail}'?\n\nThis action is irreversible and deletes all data including users, students, and teachers.`)) {
      return;
    }
    try {
      const res = await axios.delete(`${API_BASE_URL}/api/superadmin/tenant/${tenantEmail}`);
      if (res.data.status === 'success') {
        toast.success(res.data.message);
        fetchTenants();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete tenant');
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    if (!form.instituteName || !form.adminEmail || !form.adminPassword) {
      toast.error('Please fill in all mandatory schema fields');
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await axios.post(API_BASE_URL + '/api/superadmin/create-tenant', {
        creatorEmail: user?.email || "superadmin@pensense.com",
        tenantEmail: form.adminEmail.trim(), // Use email as the unique identifier
        adminName: form.instituteName.trim(),
        adminEmail: form.adminEmail.trim(),
        adminPassword: form.adminPassword,
        logoUrl: form.logoUrl,
        themeColor: form.themeColor,
        role: form.role,
        parentTenantEmail: form.parentTenantEmail,
      });

      if (res.data.status === 'success') {
        toast.success(res.data.message);
        setForm({ instituteName: '', adminEmail: '', adminPassword: '', logoUrl: '', themeColor: '#3b82f6', role: 'institute', parentTenantEmail: '' });
        fetchTenants();
      } else {
        toast.error(res.data.message || 'Provisioning sequence failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Server connection malfunctioned');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoUpload = async (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', 'Institution Logo');
    formData.append('subject', 'Branding');
    formData.append('type', 'LOGO');

    const toastId = toast.loading('Uploading logo...');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/files/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${user?.token}`
        }
      });
      
      const fileUrl = `${API_BASE_URL}/api/files/view/${res.data.id}`;
      
      if (isEdit) {
        setEditForm(prev => ({ ...prev, logoUrl: fileUrl }));
      } else {
        setForm(prev => ({ ...prev, logoUrl: fileUrl }));
      }
      
      toast.update(toastId, { 
        render: 'Logo uploaded successfully', 
        type: 'success', 
        isLoading: false, 
        autoClose: 3000 
      });
    } catch (err) {
      toast.update(toastId, { 
        render: 'Upload failed: ' + (err.response?.data || err.message), 
        type: 'error', 
        isLoading: false, 
        autoClose: 3000 
      });
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-8 rounded-[2rem] bg-indigo-500/10 border border-indigo-500/20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl -mr-20 -mt-20"></div>
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-400 text-sm font-semibold mb-4 border border-indigo-500/30">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"></path><path d="M2 17l10 5 10-5"></path><path d="M2 12l10 5 10-5"></path></svg>
            ROOT ACCESS LEVEL
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Instance Provisioning Portal</h1>
          <p className="text-slate-400 text-lg max-w-2xl">
            You are operating within the Superadmin bounds. Provision dynamically loaded Institute boundaries mapped natively across the Discriminator schemas.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-8 backdrop-blur-xl h-full shadow-2xl">
            <h2 className="text-xl font-bold text-white mb-6 border-b border-slate-800/60 pb-4">
              Create New Institute Tenant
            </h2>
            <form onSubmit={handleCreateTenant} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-400">Institute Name <span className="text-rose-400">*</span></label>
                <input
                  type="text"
                  name="instituteName"
                  value={form.instituteName}
                  onChange={handleChange}
                  placeholder="e.g. Delhi Public School"
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-colors placeholder:text-slate-600"
                  required
                />
                <p className="text-xs text-slate-500">The display name of the institute. Spaces allowed, duplicates allowed.</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-400">Institute Admin Email <span className="text-rose-400">*</span></label>
                <input
                  type="email"
                  name="adminEmail"
                  value={form.adminEmail}
                  onChange={handleChange}
                  placeholder="admin@school.edu"
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-colors placeholder:text-slate-600"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-400">Institute Admin Password <span className="text-rose-400">*</span></label>
                <input
                  type="password"
                  name="adminPassword"
                  value={form.adminPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-colors placeholder:text-slate-600 font-mono"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400">Logo URL (Optional)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="logoUrl"
                      value={form.logoUrl}
                      onChange={handleChange}
                      placeholder="https://example.com/logo.png"
                      className="flex-1 bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-colors placeholder:text-slate-600"
                    />
                    <label className="cursor-pointer p-3 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 rounded-xl hover:bg-indigo-500/20 transition-all flex items-center justify-center group">
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*" 
                        onChange={(e) => handleLogoUpload(e, false)} 
                      />
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="group-hover:scale-110 transition-transform"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400">Theme Color</label>
                  <div className="flex gap-3">
                    <input
                      type="color"
                      name="themeColor"
                      value={form.themeColor}
                      onChange={handleChange}
                      className="h-12 w-12 rounded-xl bg-slate-950 border border-slate-800 cursor-pointer overflow-hidden p-0"
                    />
                    <input
                      type="text"
                      name="themeColor"
                      value={form.themeColor}
                      onChange={handleChange}
                      placeholder="#3b82f6"
                      className="flex-1 bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 uppercase font-mono"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400">Tenant Role</label>
                  <select
                    name="role"
                    value={form.role}
                    onChange={handleChange}
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="institute">🏢 Institute Instance (Default)</option>
                    <option value="super_tenant">🚀 Super Tenant Hub</option>
                  </select>
                </div>

                {form.role === 'institute' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-400">Parent Super Tenant</label>
                    <select
                      name="parentTenantEmail"
                      value={form.parentTenantEmail}
                      onChange={handleChange}
                      className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
                    >
                      <option value="">-- No Parent (Independent) --</option>
                      {tenants.filter(t => t.role === 'super_tenant').map(t => (
                        <option key={t.tenantEmail} value={t.tenantEmail}>
                          👑 {t.adminName} ({t.tenantEmail})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-slate-800/60 flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-8 py-3 rounded-xl font-bold flex items-center gap-2 transition-all ${
                    isSubmitting 
                      ? 'bg-indigo-500/50 text-white/50 cursor-not-allowed' 
                      : 'bg-indigo-500 hover:bg-indigo-600 text-white shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 hover:-translate-y-0.5'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      Provisioning Instance...
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                      Execute Provisioning Blueprint
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div>
          <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-8 backdrop-blur-xl h-full shadow-2xl relative overflow-hidden flex flex-col justify-center items-center text-center">
            <div className="w-20 h-20 bg-indigo-500/10 rounded-full flex items-center justify-center mb-6 border border-indigo-500/20 shadow-inner">
              <svg className="w-10 h-10 text-indigo-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-10-5-10 5z"></path><path d="M12 15V3"></path><path d="m5 11l7-3 7 3"></path></svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Multi-Tenant DB Architecture</h3>
            <p className="text-sm text-slate-400 leading-relaxed max-w-[250px]">
              Upon successful creation, Hibernate isolates this new tenant across 9 core relational database sheets using native `tenant_email` mappings dynamically integrated throughout the entire application ecosystem.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-8 backdrop-blur-xl shadow-2xl mt-8">
        <h2 className="text-xl font-bold text-white mb-6 border-b border-slate-800/60 pb-4">
          Provisioned Tenants Registry
        </h2>
        
        {loadingTenants ? (
          <div className="flex justify-center p-8">
            <div className="w-8 h-8 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
          </div>
        ) : tenants.length === 0 ? (
          <div className="text-center p-8 text-slate-500">No tenants provisioned yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400 text-sm">
                  <th className="pb-3 font-semibold">Institute Name</th>
                  <th className="pb-3 font-semibold">Branding</th>
                  <th className="pb-3 font-semibold">Institute Email</th>
                  <th className="pb-3 font-semibold">Role / Parent Mapping</th>
                  <th className="pb-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.tenantEmail} className="border-b border-slate-800/50 hover:bg-slate-800/20 transition-colors">
                    <td className="py-4 text-white font-semibold text-sm">{t.adminName}</td>
                    <td className="py-4">
                      <div className="flex items-center gap-3">
                        {t.logoUrl ? (
                          <img src={t.logoUrl} alt="Logo" className="w-8 h-8 rounded-lg object-contain bg-slate-800 p-1 border border-slate-700" />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7"></path><line x1="16" y1="5" x2="22" y2="5"></line><line x1="19" y1="2" x2="19" y2="8"></line><circle cx="9" cy="9" r="2"></circle><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path></svg>
                          </div>
                        )}
                        <div className="w-4 h-4 rounded-full border border-slate-700" style={{ backgroundColor: t.themeColor || '#3b82f6' }}></div>
                      </div>
                    </td>
                    <td className="py-4 text-slate-300 font-mono text-sm">{t.adminEmail}</td>
                    <td className="py-4">
                      {t.role === 'super_tenant' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-400 text-xs font-bold border border-indigo-500/30">
                          👑 Super Tenant
                        </span>
                      ) : (
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex items-center w-fit gap-1.5 px-3 py-1 rounded-full bg-slate-800 text-slate-400 text-xs font-semibold border border-slate-700">
                            🏢 Institute
                          </span>
                          {t.parentTenantEmail && (
                            <span className="text-[10px] text-slate-500 italic truncate max-w-[200px]">
                              Parent: {t.parentTenantEmail}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="py-4 flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setEditingTenant(t);
                          setEditForm({
                            schoolName: t.schoolName || t.adminName,
                            logoUrl: t.logoUrl || '',
                            themeColor: t.themeColor || '#3b82f6',
                            role: t.role || 'institute',
                            parentTenantEmail: t.parentTenantEmail || ''
                          });
                        }}
                        className="p-2 text-indigo-400 hover:text-white hover:bg-indigo-500/20 rounded-lg transition-colors inline-flex items-center gap-2 text-sm"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteTenant(t.tenantEmail)}
                        className="p-2 text-rose-400 hover:text-white hover:bg-rose-500/20 rounded-lg transition-colors inline-flex items-center gap-2 text-sm"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-8 backdrop-blur-xl shadow-2xl mt-8">
        <h2 className="text-xl font-bold text-white mb-6 border-b border-slate-800/60 pb-4 flex items-center justify-between">
          <span>Global AI Configuration</span>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${aiProvider === 'gemini' ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]' : 'bg-slate-500'}`}></span>
            <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">System Active</span>
          </div>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div className="space-y-4">
            <p className="text-slate-400 text-sm leading-relaxed">
              Select the primary LLM engine for processing schedule generation, student analysis, and automated teaching assistance. OpenAI now utilizes GPT-4o for flagship accuracy.
            </p>
            
            <div className="flex flex-col gap-3">
              <button 
                onClick={() => updateConfig('openai')}
                className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${
                  aiProvider === 'openai' 
                    ? 'bg-indigo-500/10 border-indigo-500 shadow-lg shadow-indigo-500/10' 
                    : 'bg-slate-950/50 border-slate-800 hover:border-slate-700'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${aiProvider === 'openai' ? 'bg-indigo-500 text-white' : 'bg-slate-800 text-slate-400'}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v10"/><path d="M18.4 6.6a9 9 0 1 1-12.77.04"/></svg>
                  </div>
                  <div className="text-left">
                    <span className="block font-bold text-white text-sm tracking-wide">OpenAI (GPT-4o / 3.5)</span>
                    <span className="text-[10px] text-slate-500 font-mono uppercase">api.openai.com v1</span>
                  </div>
                </div>
                {aiProvider === 'openai' && (
                  <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center shadow-inner">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </div>
                )}
              </button>

              <button 
                onClick={() => updateConfig('gemini')}
                className={`flex items-center justify-between p-4 rounded-2xl border transition-all ${
                  aiProvider === 'gemini' 
                    ? 'bg-emerald-500/10 border-emerald-500 shadow-lg shadow-emerald-500/10' 
                    : 'bg-slate-950/50 border-slate-800 hover:border-slate-700'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${aiProvider === 'gemini' ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-slate-400'}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v19"/><path d="m5 8 7-3 7 3"/><path d="m5 16 7 3 7-3"/></svg>
                  </div>
                  <div className="text-left">
                    <span className="block font-bold text-white text-sm tracking-wide">Google Gemini (Flash)</span>
                    <span className="text-[10px] text-slate-500 font-mono uppercase">generativelanguage.v1beta</span>
                  </div>
                </div>
                {aiProvider === 'gemini' && (
                  <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shadow-inner">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                  </div>
                )}
              </button>
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-indigo-500/5 border border-indigo-500/10 h-full flex flex-col justify-center">
             <div className="flex items-center gap-3 mb-4 text-indigo-400">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                <h4 className="font-bold text-sm uppercase tracking-widest">Instance Integrity</h4>
             </div>
             <p className="text-xs text-slate-400 leading-relaxed">
               Switching providers affects internal scheduling algorithms and teaching assistants globally. Ensure valid API keys are configured in 01_system_env before production deployment.
             </p>
             <div className="mt-6 pt-4 border-t border-indigo-500/10 flex items-center gap-4">
               <div className="flex -space-x-2">
                 {[1,2,3].map(i => <div key={i} className="w-6 h-6 rounded-full border-2 border-slate-900 bg-slate-800"></div>)}
               </div>
               <span className="text-[10px] text-slate-500 font-medium">Currently active in {tenants.length} instances</span>
             </div>
          </div>
        </div>
      </div>

      {/* Global WhatsApp API Settings */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-8 backdrop-blur-xl shadow-2xl mt-8">
        <h2 className="text-xl font-bold text-white mb-6 border-b border-slate-800/60 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
            </div>
            <div>
              Global WhatsApp API Settings
              <p className="text-xs text-slate-400 font-normal mt-1">Configure your Meta Cloud API for automated student messaging.</p>
            </div>
          </div>
          <button 
            onClick={saveGlobalWaConfig}
            disabled={savingWaConfig}
            className="px-6 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50"
          >
            {savingWaConfig ? 'Saving...' : 'Save Settings'}
          </button>
        </h2>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Meta API Key (Permanent Token)</label>
            <input 
              type="password"
              placeholder="EAA..."
              value={globalWaConfig.whatsappApiKey}
              onChange={e => setGlobalWaConfig({...globalWaConfig, whatsappApiKey: e.target.value})}
              className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 font-mono text-sm"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Phone Number ID</label>
              <input 
                type="text"
                placeholder="11495..."
                value={globalWaConfig.whatsappPhoneNumberId}
                onChange={e => setGlobalWaConfig({...globalWaConfig, whatsappPhoneNumberId: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Business Account ID</label>
              <input 
                type="text"
                placeholder="15332..."
                value={globalWaConfig.whatsappBusinessAccountId}
                onChange={e => setGlobalWaConfig({...globalWaConfig, whatsappBusinessAccountId: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 font-mono text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Meta Webhook Verify Token</label>
              <input 
                type="text"
                placeholder="e.g. pensense_secure_token_123"
                value={globalWaConfig.metaWebhookVerifyToken}
                onChange={e => setGlobalWaConfig({...globalWaConfig, metaWebhookVerifyToken: e.target.value})}
                className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">WhatsApp Webhook URL</label>
              <div className="flex gap-4">
                <input 
                  type="text"
                  readOnly
                  value="https://pensense.tech/api/webhooks/whatsapp"
                  className="flex-1 bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-emerald-400 font-mono text-sm select-all"
                />
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText("https://pensense.tech/api/webhooks/whatsapp");
                    toast.success("Webhook URL copied to clipboard!");
                  }}
                  className="px-6 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold transition-all flex items-center gap-2 whitespace-nowrap"
                >
                  Copy Link
                </button>
              </div>
            </div>
          </div>



          <div className="p-6 rounded-2xl bg-slate-950/20 border border-slate-800/50 space-y-4">
            <div className="flex items-center gap-2 mb-2 text-emerald-400">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
              <span className="text-xs font-bold uppercase tracking-widest">Real-Time Delivery Active</span>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Study Time Template</label>
              <div className="flex gap-4">
                <select 
                  value={globalWaConfig.whatsappStudyTimeTemplate}
                  onChange={e => setGlobalWaConfig({...globalWaConfig, whatsappStudyTimeTemplate: e.target.value})}
                  className="flex-1 bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 text-sm"
                >
                  <option value="">-- Select Template --</option>
                  {whatsappTemplates.map((t, idx) => (
                    <option key={idx} value={t}>{t}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleSendStudyTime}
                  disabled={sendingStudyTime}
                  className="px-6 py-3 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white font-bold transition-all shadow-lg shadow-indigo-500/20 whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
                >
                  {sendingStudyTime ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      Broadcasting...
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                      Broadcast Now
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-8 backdrop-blur-xl shadow-2xl mt-8">
        <h2 className="text-xl font-bold text-white mb-6 border-b border-slate-800/60 pb-4">
          Development & Testing Tools
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="p-6 rounded-2xl bg-blue-500/5 border border-blue-500/10 hover:border-blue-500/30 transition-all group">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center text-blue-400 group-hover:scale-110 transition-transform">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
              </div>
              <div>
                <h3 className="font-bold text-white">Question Bank Test</h3>
                <p className="text-xs text-slate-500">CMS_test (Different Table)</p>
              </div>
            </div>
            <p className="text-sm text-slate-400 mb-6">
              Access the testing version of the CMS. This links to the `question_bank_test` table instead of the production one.
            </p>
            <a 
              href={`/cms.com/lms/cms-test`} 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-bold transition-all shadow-lg shadow-blue-500/20"
            >
              Open CMS_test
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
            </a>
          </div>
        </div>
      </div>
      <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-8 backdrop-blur-xl shadow-2xl mt-8">
        <h2 className="text-xl font-bold text-white mb-6 border-b border-slate-800/60 pb-4">
          Student-Teacher Direct Assignment
        </h2>
        <div className="space-y-6">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-slate-400">Select Tenant to Manage</label>
            <select
              value={selectedTenantForAssignment}
              onChange={(e) => fetchTenantAssignmentData(e.target.value)}
              className="w-full md:w-1/2 bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
            >
              <option value="">-- Choose Tenant --</option>
              {tenants.map(t => (
                <option key={t.tenantEmail} value={t.tenantEmail}>
                  {t.adminName} ({t.tenantEmail})
                </option>
              ))}
            </select>
          </div>

          {selectedTenantForAssignment && (
            <div className="mt-6 border border-slate-800 rounded-2xl overflow-hidden">
              {loadingAssignments ? (
                <div className="flex justify-center p-12">
                  <div className="w-8 h-8 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-950 border-b border-slate-800 text-slate-400 text-sm">
                        <th className="p-4 font-semibold">Student Name</th>
                        <th className="p-4 font-semibold text-center w-32">WhatsApp</th>
                        <th className="p-4 font-semibold">Student Email</th>
                        <th className="p-4 font-semibold">Assigned Teachers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenantStudents.length === 0 ? (
                        <tr><td colSpan="4" className="p-8 text-center text-slate-500">No students found.</td></tr>
                      ) : (
                        tenantStudents.map(student => {
                          const studentAssignments = tenantAssignments
                            .filter(a => a.student_email === student.email)
                            .map(a => a.teacher_email);
                            
                          return (
                            <tr key={student.id} className="border-b border-slate-800/50 hover:bg-slate-800/20">
                              <td className="p-4 text-white text-sm font-medium">{student.name}</td>
                              <td className="p-4">
                                <div className="flex justify-center">
                                  <button
                                    onClick={() => toggleStudentWhatsapp(student.id)}
                                    title={student.whatsappEnabled ? "Disable WhatsApp Alerts" : "Enable WhatsApp Alerts"}
                                    className={`w-6 h-6 rounded border flex items-center justify-center transition-all ${
                                      student.whatsappEnabled 
                                        ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-500/20' 
                                        : 'bg-slate-900 border-slate-700 text-transparent hover:border-slate-500'
                                    }`}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                  </button>
                                </div>
                              </td>
                              <td className="p-4 text-slate-400 text-sm">{student.email}</td>
                              <td className="p-4">
                                <div className="flex flex-wrap gap-2">
                                  {tenantTeachers.map(teacher => {
                                    const isAssigned = studentAssignments.includes(teacher.email);
                                    return (
                                      <button
                                        key={teacher.email}
                                        onClick={() => toggleTeacherAssignment(student.id, student.email, teacher.email)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                          isAssigned
                                            ? 'bg-indigo-500/20 border-indigo-500 text-indigo-300'
                                            : 'bg-slate-900 border-slate-700 text-slate-500 hover:border-slate-500'
                                        }`}
                                      >
                                        {teacher.name}
                                        {isAssigned && (
                                          <svg className="inline-block ml-1.5 w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                        )}
                                      </button>
                                    );
                                  })}
                                  {tenantTeachers.length === 0 && (
                                    <span className="text-slate-600 text-xs italic">No teachers available</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Edit Branding Modal */}
      {editingTenant && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={() => setEditingTenant(null)}></div>
          <div className="relative w-full max-w-lg bg-slate-900 border border-slate-800 rounded-3xl p-8 shadow-2xl animate-in zoom-in-95 duration-200">
            <h3 className="text-xl font-bold text-white mb-6">Edit Branding: {editingTenant.adminName}</h3>
            
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-400">Public School Name</label>
                <input
                  type="text"
                  value={editForm.schoolName}
                  onChange={(e) => setEditForm({...editForm, schoolName: e.target.value})}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-400">Logo URL</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editForm.logoUrl}
                    onChange={(e) => setEditForm({...editForm, logoUrl: e.target.value})}
                    className="flex-1 bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
                  />
                  <label className="cursor-pointer p-3 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 rounded-xl hover:bg-indigo-500/20 transition-all flex items-center justify-center group">
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*" 
                      onChange={(e) => handleLogoUpload(e, true)} 
                    />
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="group-hover:scale-110 transition-transform"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-400">Theme Color</label>
                <div className="flex gap-3">
                  <input
                    type="color"
                    value={editForm.themeColor}
                    onChange={(e) => setEditForm({...editForm, themeColor: e.target.value})}
                    className="h-12 w-12 rounded-xl bg-slate-950 border border-slate-800 cursor-pointer p-0 overflow-hidden"
                  />
                  <input
                    type="text"
                    value={editForm.themeColor}
                    onChange={(e) => setEditForm({...editForm, themeColor: e.target.value})}
                    className="flex-1 bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 uppercase font-mono"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-400">Tenant Role</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
                >
                  <option value="institute">🏢 Institute Instance</option>
                  <option value="super_tenant">🚀 Super Tenant Hub</option>
                </select>
              </div>

              {editForm.role === 'institute' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400">Parent Super Tenant</label>
                  <select
                    value={editForm.parentTenantEmail}
                    onChange={(e) => setEditForm({...editForm, parentTenantEmail: e.target.value})}
                    className="w-full bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">-- No Parent (Independent) --</option>
                    {tenants.filter(t => t.role === 'super_tenant' && t.tenantEmail !== editingTenant.tenantEmail).map(t => (
                      <option key={t.tenantEmail} value={t.tenantEmail}>
                        👑 {t.adminName} ({t.tenantEmail})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="pt-6 flex gap-3">
                <button
                  onClick={() => setEditingTenant(null)}
                  className="flex-1 px-6 py-3 rounded-xl font-bold text-slate-400 hover:bg-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.put(`${API_BASE_URL}/api/superadmin/update-branding`, {
                        tenantEmail: editingTenant.tenantEmail,
                        ...editForm
                      });
                      if (res.data.status === 'success') {
                        toast.success('Branding updated successfully');
                        setEditingTenant(null);
                        fetchTenants();
                      }
                    } catch (err) {
                      toast.error('Failed to update branding');
                    }
                  }}
                  className="flex-1 px-6 py-3 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white font-bold shadow-lg shadow-indigo-500/25 transition-all"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
